import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Input } from '../components/ui/input';
import {
  CheckCircle,
  Loader2,
  ShieldCheck,
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUESTIONS = [
  { key: 'objectives_met', label: 'Did the session meet the stated learning objectives?' },
  { key: 'evidence_based', label: 'Was the content evidence-based and balanced?' },
  { key: 'commercial_bias', label: 'Was there any perceived commercial bias?' },
  { key: 'clinical_influence', label: 'Will this activity influence your clinical practice?' },
];

const emptyAnswer = () => ({ answer: '', other_text: '' });

export default function AnonymousFeedback() {
  const { inviteCode } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    objectives_met: emptyAnswer(),
    evidence_based: emptyAnswer(),
    commercial_bias: emptyAnswer(),
    clinical_influence: emptyAnswer(),
    suggestions: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/public/events/${inviteCode}/feedback-meta`);
        setMeta(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'This feedback link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [inviteCode]);

  const setAnswer = (key, answer) =>
    setFormData((p) => ({ ...p, [key]: { ...p[key], answer } }));

  const setOther = (key, other_text) =>
    setFormData((p) => ({ ...p, [key]: { ...p[key], other_text } }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const unanswered = QUESTIONS.filter((q) => !formData[q.key].answer);
    if (unanswered.length > 0) {
      toast.error('Please answer all questions');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/events/${inviteCode}/feedback-anonymous`, formData);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Link not available</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <Toaster richColors position="top-center" />
        <Card className="max-w-md w-full" data-testid="anonymous-feedback-submitted">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Thank You!</h2>
            <p className="text-muted-foreground">
              Your feedback for <strong>{meta?.title}</strong> has been recorded anonymously.
              We appreciate you taking the time.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const branding = meta?.branding || {};
  const primary = branding.primary_color || '#0F172A';
  const accent = branding.accent_color || '#0EA5E9';

  return (
    <div className="min-h-screen bg-background" data-testid="anonymous-feedback-page">
      <Toaster richColors position="top-center" />

      <div
        className="border-b border-border"
        style={{ background: `linear-gradient(135deg, ${primary} 0%, ${primary}DD 60%, ${accent} 100%)` }}
      >
        <div className="max-w-2xl mx-auto px-6 py-8 text-white">
          <div className="flex items-start gap-4 flex-wrap">
            {branding.logo_url && (
              <img
                src={resolveAssetUrl(branding.logo_url)}
                alt={branding.organization_name}
                className="h-14 w-14 rounded-lg bg-white p-2 object-contain shadow-md"
                data-testid="anonymous-feedback-logo"
              />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs uppercase tracking-widest text-white/80 font-semibold">
                Anonymous Feedback
              </p>
              <h1 className="text-2xl sm:text-3xl font-bold mt-1" data-testid="anonymous-feedback-event-title">
                {meta?.title}
              </h1>
              {(meta?.date || meta?.venue) && (
                <p className="text-white/90 mt-1 text-sm">
                  {meta.date}{meta.venue ? ` · ${meta.venue}` : ''}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-6 py-10">
        <Card className="border-border">
          <CardHeader>
            <CardTitle>Share your feedback</CardTitle>
            <CardDescription>
              Your responses are anonymous. This helps {meta?.org_name || 'IIDA'} improve future educational activities.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-2 p-3 mb-6 bg-sky-50 border border-sky-200 rounded-lg text-sky-800 text-sm" data-testid="anonymous-feedback-banner">
              <ShieldCheck className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>
                No identifying information is collected. You may complete this form once per device.
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8" data-testid="anonymous-feedback-form">
              {QUESTIONS.map((q) => (
                <div key={q.key} className="space-y-3" data-testid={`q-${q.key}`}>
                  <Label className="text-base font-medium">{q.label}</Label>
                  <RadioGroup
                    value={formData[q.key].answer}
                    onValueChange={(v) => setAnswer(q.key, v)}
                    className="flex flex-col gap-3"
                  >
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <RadioGroupItem value="yes" id={`${q.key}-yes`} data-testid={`q-${q.key}-yes`} />
                      <span className="font-normal">Yes</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <RadioGroupItem value="no" id={`${q.key}-no`} data-testid={`q-${q.key}-no`} />
                      <span className="font-normal">No</span>
                    </label>
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <RadioGroupItem value="other" id={`${q.key}-other`} data-testid={`q-${q.key}-other`} />
                      <span className="font-normal">Other</span>
                    </label>
                    {formData[q.key].answer === 'other' && (
                      <Input
                        placeholder="Please specify..."
                        value={formData[q.key].other_text}
                        onChange={(e) => setOther(q.key, e.target.value)}
                        className="ml-7"
                        data-testid={`q-${q.key}-other-input`}
                      />
                    )}
                  </RadioGroup>
                </div>
              ))}

              <div className="space-y-2">
                <Label htmlFor="suggestions" className="text-base font-medium">
                  Suggestions for improvement (optional)
                </Label>
                <Textarea
                  id="suggestions"
                  placeholder="Any feedback to help us improve future events..."
                  value={formData.suggestions}
                  onChange={(e) => setFormData({ ...formData, suggestions: e.target.value })}
                  rows={4}
                  data-testid="anonymous-feedback-suggestions"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12"
                disabled={submitting}
                data-testid="anonymous-feedback-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting…
                  </>
                ) : (
                  'Submit feedback'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
